import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import {
  getAirdrops,
  getUserAirdrops,
  participateInAirdrop,
} from "../../store/slices/airdropSlice";
import Header from "../../components/Layout/Header";
import BottomNav from "../../components/Layout/BottomNav";
import Card from "../../components/UI/Card";
import Button from "../../components/UI/Button";
import Modal from "../../components/UI/Modal";
import Alert from "../../components/UI/Alert";
import globalStyle from "../../styles/globalStyle";
import {
  formatXRP, // Added formatXRP function
} from "../../utils/formatters";

import {
  GiftIcon,
  ClockIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  CalendarIcon,
  UsersIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";

const Airdrops: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { airdrops, userAirdrops, isLoading, isParticipating, error } =
    useSelector((state: RootState) => state.airdrop);

  const [selectedAirdrop, setSelectedAirdrop] = useState<any>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showParticipationModal, setShowParticipationModal] = useState(false);
  const [participationError, setParticipationError] = useState<string | null>(
    null,
  );

  useEffect(() => {
    loadAirdrops();
    loadUserAirdrops();
    // eslint-disable-next-line
  }, [dispatch]);

  const loadAirdrops = () => {
    dispatch(getAirdrops());
    // eslint-disable-next-line
  };

  const loadUserAirdrops = () => {
    dispatch(getUserAirdrops());
    // eslint-disable-next-line
  };

  const handleParticipate = async () => {
    if (!selectedAirdrop) return;

    setParticipationError(null);
    const result = await dispatch(participateInAirdrop(selectedAirdrop.id));

    if (participateInAirdrop.fulfilled.match(result)) {
      setShowParticipationModal(false);
      setShowDetails(false);
      setSelectedAirdrop(null);
      // Reload data
      loadAirdrops();
      loadUserAirdrops();
    } else if (participateInAirdrop.rejected.match(result)) {
      setParticipationError(
        (result.payload as string) || "Failed to participate in airdrop",
      );
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  const getStatusBadge = (status: string) => {
    const baseClass = `${globalStyle.badge} px-2 py-1`;
    switch (status) {
      case "active":
        return (
          <span className={`${baseClass} ${globalStyle.badgeSuccess}`}>
            Active
          </span>
        );
      case "pending":
        return (
          <span className={`${baseClass} ${globalStyle.badgeWarning}`}>
            Pending
          </span>
        );
      case "completed":
        return (
          <span className={`${baseClass} ${globalStyle.badgeInfo}`}>
            Completed
          </span>
        );
      case "cancelled":
        return (
          <span className={`${baseClass} ${globalStyle.badgeDanger}`}>
            Cancelled
          </span>
        );
      default:
        return (
          <span className={`${baseClass} ${globalStyle.badgeInfo}`}>
            {status}
          </span>
        );
    }
  };

  const isUserEligible = (airdrop: any) => {
    // Check if user has already received this airdrop
    const userReceived = userAirdrops.some((ua: any) => ua.id === airdrop.id);
    if (userReceived) return false;

    // Check if airdrop is active
    if (airdrop.status !== "active") return false;

    // Check if distribution date is in the future
    if (
      airdrop.distribution_date &&
      new Date(airdrop.distribution_date) > new Date()
    ) {
      return false;
    }

    // Additional eligibility checks would go here based on criteria
    return true;
  };

  const hasUserReceived = (airdropId: number) => {
    return userAirdrops.some((airdrop: any) => airdrop.id === airdropId);
  };

  const getReceivedAirdropInfo = (airdropId: number) => {
    const received = userAirdrops.find(
      (airdrop: any) => airdrop.id === airdropId,
    );
    if (received) {
      return {
        amount: received.pivot?.amount || received.amount,
        distributedAt: received.pivot?.distributed_at,
      };
    }
    return null;
  };

  // Helper function to safely format XRP amounts
  const formatXRPSafe = (amount: any) => {
    if (amount === undefined || amount === null) return "0.00";

    const num =
      typeof amount === "string" ? parseFloat(amount) : Number(amount);
    if (isNaN(num)) return "0.00";

    return formatXRP(num);
  };

  return (
    <div className={globalStyle.container}>
      <Header />

      <main className="page-content">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className={globalStyle.h3}>Airdrops</h1>
            <p className={globalStyle.body2 + " text-gray-400"}>
              Participate in exclusive airdrop campaigns
            </p>
          </div>
          <div className="mt-4 md:mt-0">
            <Button
              variant="secondary"
              onClick={() => {
                loadAirdrops();
                loadUserAirdrops();
              }}
              loading={isLoading}
            >
              <ArrowPathIcon className="w-4 h-4 mr-2 text-primary" />
              Refresh
            </Button>
          </div>
        </div>

        {/* My Airdrops */}
        {userAirdrops.length > 0 && (
          <Card className="mb-6" padding="lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className={globalStyle.h5}>My Airdrops</h3>
              <span className={globalStyle.caption + " text-gray-400"}>
                {userAirdrops.length} received
              </span>
            </div>

            <div className="space-y-3">
              {userAirdrops.map((airdrop: any) => {
                const receivedInfo = getReceivedAirdropInfo(airdrop.id);
                return (
                  <div
                    key={airdrop.id}
                    className="flex items-center justify-between p-4 border border-gray-800 rounded-lg hover:bg-gray-800 transition-colors cursor-pointer"
                    onClick={() => {
                      setSelectedAirdrop(airdrop);
                      setShowDetails(true);
                    }}
                  >
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-green-900 flex items-center justify-center mr-3">
                        <GiftIcon className="w-5 h-5 text-green-500" />
                      </div>
                      <div>
                        <p className="font-medium text-white">{airdrop.name}</p>
                        <p className={globalStyle.caption + " text-gray-400"}>
                          {receivedInfo?.distributedAt
                            ? `Received ${formatDate(
                                receivedInfo.distributedAt,
                              )}`
                            : "Pending distribution"}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-green-500">
                        +{formatXRPSafe(receivedInfo?.amount || airdrop.amount)}{" "}
                        XRP
                      </p>
                      <p className={globalStyle.caption + " text-gray-400"}>
                        {airdrop.status === "completed"
                          ? "Completed"
                          : "Pending"}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Available Airdrops */}
        <Card padding="lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className={globalStyle.h5}>Available Airdrops</h3>
            <div className="flex items-center space-x-2">
              <span className={globalStyle.caption + " text-gray-400"}>
                {airdrops.filter((a: any) => a.status === "active").length}{" "}
                active
              </span>
            </div>
          </div>

          {error && <Alert type="error" message={error} className="mb-6" />}
          {participationError && (
            <Alert type="error" message={participationError} className="mb-6" />
          )}

          {isLoading ? (
            <div className="text-center py-12">
              <svg
                className={`${globalStyle.loadingSpinner} mx-auto`}
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <p className={`${globalStyle.body2} mt-4 text-gray-400`}>
                Loading airdrops...
              </p>
            </div>
          ) : airdrops.length === 0 ? (
            <div className="text-center py-12">
              <GiftIcon className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <h3 className={globalStyle.h5}>No Airdrops Available</h3>
              <p className={`${globalStyle.body2} mt-2 text-gray-400`}>
                Check back later for new airdrop opportunities.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {airdrops.map((airdrop: any) => {
                const isEligible = isUserEligible(airdrop);
                const hasReceived = hasUserReceived(airdrop.id);

                return (
                  <Card
                    key={airdrop.id}
                    hover
                    padding="lg"
                    className={`cursor-pointer ${
                      hasReceived ? "opacity-75" : ""
                    }`}
                    onClick={() => {
                      setSelectedAirdrop(airdrop);
                      setShowDetails(true);
                    }}
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                      <div className="mb-4 md:mb-0 flex-1">
                        <div className="flex items-center mb-2">
                          <GiftIcon className="w-5 h-5 text-primary mr-2" />
                          <h4 className={globalStyle.h6}>{airdrop.name}</h4>
                          <div className="ml-2">
                            {getStatusBadge(airdrop.status)}
                          </div>
                          {hasReceived && (
                            <div className="ml-2">
                              <span
                                className={`${globalStyle.badge} ${globalStyle.badgeSuccess}`}
                              >
                                Received
                              </span>
                            </div>
                          )}
                        </div>
                        <p className={`${globalStyle.body2} text-gray-400`}>
                          {airdrop.description}
                        </p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {airdrop.distribution_date && (
                            <div className="flex items-center text-sm text-gray-500">
                              <CalendarIcon className="w-3 h-3 mr-1" />
                              {formatDate(airdrop.distribution_date)}
                            </div>
                          )}

                          {airdrop.total_distributed && (
                            <div className="flex items-center text-sm text-gray-500">
                              <GiftIcon className="w-3 h-3 mr-1" />
                              {formatXRPSafe(airdrop.total_distributed)} XRP
                              distributed
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="text-center md:text-right">
                        <p className={`${globalStyle.h4} text-primary`}>
                          {formatXRPSafe(airdrop.amount)} XRP
                        </p>
                        {isEligible && (
                          <Button
                            size="sm"
                            className="mt-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedAirdrop(airdrop);
                              setShowParticipationModal(true);
                            }}
                          >
                            Participate
                          </Button>
                        )}
                        {hasReceived && (
                          <p
                            className={`${globalStyle.caption} mt-2 text-green-500`}
                          >
                            ✓ Already received
                          </p>
                        )}
                        {airdrop.status === "pending" && (
                          <p
                            className={`${globalStyle.caption} mt-2 text-yellow-500`}
                          >
                            <ClockIcon className="w-3 h-3 inline mr-1" />
                            Starting soon
                          </p>
                        )}
                        {airdrop.status === "completed" && !hasReceived && (
                          <p
                            className={`${globalStyle.caption} mt-2 text-gray-500`}
                          >
                            Completed
                          </p>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </Card>
      </main>

      <BottomNav />

      {/* Airdrop Details Modal */}
      <Modal
        isOpen={showDetails}
        onClose={() => {
          setShowDetails(false);
          setSelectedAirdrop(null);
        }}
        title={selectedAirdrop?.name}
        size="lg"
      >
        {selectedAirdrop && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className={`${globalStyle.h6} text-gray-400`}>Amount</h4>
                <p className={`${globalStyle.h4} text-primary`}>
                  {formatXRPSafe(selectedAirdrop.amount)} XRP
                </p>
              </div>
              <div>
                <h4 className={`${globalStyle.h6} text-gray-400`}>Status</h4>
                {getStatusBadge(selectedAirdrop.status)}
              </div>
              {selectedAirdrop.distribution_date && (
                <div>
                  <h4 className={`${globalStyle.h6} text-gray-400`}>
                    Distribution Date
                  </h4>
                  <p className="text-white">
                    {formatDateTime(selectedAirdrop.distribution_date)}
                  </p>
                </div>
              )}
            </div>

            <div>
              <h4 className={`${globalStyle.h6} text-gray-400`}>Description</h4>
              <p className={globalStyle.body2 + " text-gray-300"}>
                {selectedAirdrop.description}
              </p>
            </div>

            {selectedAirdrop.criteria &&
              Object.keys(selectedAirdrop.criteria).length > 0 && (
                <div>
                  <h4 className={`${globalStyle.h6} text-gray-400 mb-2`}>
                    Eligibility Criteria
                  </h4>
                  <div className="bg-gray-800 p-4 rounded-lg">
                    <ul className="space-y-2">
                      {selectedAirdrop.criteria.min_balance && (
                        <li className="flex items-center text-gray-300">
                          <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2" />
                          Minimum balance:{" "}
                          {formatXRPSafe(selectedAirdrop.criteria.min_balance)}{" "}
                          XRP
                        </li>
                      )}
                      {selectedAirdrop.criteria.registration_before && (
                        <li className="flex items-center text-gray-300">
                          <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2" />
                          Registered before:{" "}
                          {formatDate(
                            selectedAirdrop.criteria.registration_before,
                          )}
                        </li>
                      )}
                      {selectedAirdrop.criteria.registration_after && (
                        <li className="flex items-center text-gray-300">
                          <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2" />
                          Registered after:{" "}
                          {formatDate(
                            selectedAirdrop.criteria.registration_after,
                          )}
                        </li>
                      )}
                      {selectedAirdrop.criteria.kyc_verified && (
                        <li className="flex items-center text-gray-300">
                          <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2" />
                          KYC verification required
                        </li>
                      )}
                      {selectedAirdrop.criteria.min_trades && (
                        <li className="flex items-center text-gray-300">
                          <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2" />
                          Minimum {selectedAirdrop.criteria.min_trades} trades
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              )}

            {hasUserReceived(selectedAirdrop.id) && (
              <div className="bg-green-900 border border-green-800 rounded-lg p-4">
                <h4 className="text-sm font-medium text-green-300 mb-2">
                  You Received This Airdrop
                </h4>
                <p className="text-sm text-green-200">
                  You received{" "}
                  {formatXRPSafe(
                    getReceivedAirdropInfo(selectedAirdrop.id)?.amount ||
                      selectedAirdrop.amount,
                  )}{" "}
                  XRP
                  {getReceivedAirdropInfo(selectedAirdrop.id)?.distributedAt &&
                    ` on ${formatDate(
                      getReceivedAirdropInfo(selectedAirdrop.id)!
                        .distributedAt!,
                    )}`}
                </p>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-800">
              <Button
                variant="tertiary"
                onClick={() => {
                  setShowDetails(false);
                  setSelectedAirdrop(null);
                }}
              >
                Close
              </Button>
              {isUserEligible(selectedAirdrop) && (
                <Button
                  onClick={() => {
                    setShowDetails(false);
                    setShowParticipationModal(true);
                  }}
                  loading={isParticipating}
                >
                  Participate Now
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Participation Confirmation Modal */}
      <Modal
        isOpen={showParticipationModal}
        onClose={() => {
          setShowParticipationModal(false);
          setSelectedAirdrop(null);
          setParticipationError(null);
        }}
        title="Confirm Participation"
        size="md"
      >
        {selectedAirdrop && (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-yellow-900 mx-auto mb-4 flex items-center justify-center">
                <GiftIcon className="w-8 h-8 text-yellow-300" />
              </div>
              <h3 className={globalStyle.h5}>{selectedAirdrop.name}</h3>
              <p className={globalStyle.body2 + " text-gray-400"}>
                You are about to participate in this airdrop campaign
              </p>
            </div>

            <div className="bg-blue-900 border border-blue-800 rounded-lg p-4">
              <div className="flex items-start">
                <InformationCircleIcon className="w-5 h-5 text-blue-300 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-blue-300 mb-2">
                    Important Information
                  </h4>
                  <ul className="text-sm text-blue-200 space-y-1">
                    <li>
                      • Airdrop amount: {formatXRPSafe(selectedAirdrop.amount)}{" "}
                      XRP
                    </li>
                    <li>
                      • Distribution date:{" "}
                      {selectedAirdrop.distribution_date
                        ? formatDate(selectedAirdrop.distribution_date)
                        : "TBD"}
                    </li>
                    <li>• You can only participate once per airdrop</li>
                    <li>• Airdrops are distributed after the campaign ends</li>
                    <li>• You must meet all eligibility criteria</li>
                  </ul>
                </div>
              </div>
            </div>

            {participationError && (
              <Alert type="error" message={participationError} />
            )}

            <div className="flex space-x-3">
              <Button
                variant="tertiary"
                onClick={() => {
                  setShowParticipationModal(false);
                  setSelectedAirdrop(null);
                  setParticipationError(null);
                }}
                fullWidth
              >
                Cancel
              </Button>
              <Button
                onClick={handleParticipate}
                loading={isParticipating}
                fullWidth
              >
                Confirm Participation
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Airdrops;
